import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CompanionPrivacy = () => {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Managerial Thinking Companion</h1>
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Privacy & Data Use Notice</h2>
        <p className="text-gray-600 mb-8">Effective date: 18th January 2026</p>
        
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-700 mb-8 text-lg">
            This notice explains how information is handled when you use the AI Managerial Thinking Companion ("the Companion") as part of the 30-Day Skills system.
          </p>
          
          <div className="bg-slate-100 border-l-4 border-red-600 p-4 mb-8">
            <p className="text-gray-700 m-0">
              The Companion is designed to support reflective thinking and leadership development — not to evaluate individuals, automate decisions, or replace human judgment.
            </p>
          </div>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. What information you may provide</h2>
            <p className="text-gray-700 mb-4">When using the Companion, you may choose to share information such as:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Descriptions of workplace situations</li>
              <li>Reflections on decisions, conversations, or challenges</li>
              <li>Context about roles, teams, or organisational dynamics</li>
            </ul>
            <p className="text-gray-700 mb-4">
              You are encouraged <strong>not to include unnecessary personal data</strong>, particularly:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Full names</li>
              <li>Sensitive personal characteristics</li>
              <li>Medical, disciplinary, or legally protected information</li>
            </ul>
            <p className="text-gray-700">
              Where such data is included, it is handled in line with the safeguards below.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How information is used</h2>
            <p className="text-gray-700 mb-4">Information shared with the Companion is used to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Generate reflective, non-prescriptive responses</li>
              <li>Help you think more clearly about leadership situations</li>
              <li>Support skill development within the 30-Day Skills framework</li>
              <li>Improve the quality and consistency of the Companion's responses over time</li>
            </ul>
            <p className="text-gray-700 mb-4">The Companion <strong>does not</strong>:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Make decisions on your behalf</li>
              <li>Evaluate or score individuals</li>
              <li>Generate performance assessments</li>
              <li>Produce recommendations for disciplinary action</li>
            </ul>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How the Companion works with your data</h2>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Reflection, not judgment</h3>
            <p className="text-gray-700 mb-4">
              The Companion analyses the <em>structure and themes</em> of what you share in order to respond thoughtfully. This may include recognising patterns such as uncertainty, tension, or decision pressure — always for the purpose of reflection and learning.
            </p>
            <p className="text-gray-700 mb-4">Any pattern recognition is:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Focused on <em>your leadership context</em></li>
              <li>Used to support your development</li>
              <li>Not used to assess or label other people</li>
            </ul>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Storage and retention</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Conversations are stored securely in pseudonymised form</li>
              <li>Personal identifiers are minimised and, where possible, hashed or redacted</li>
              <li>Data is retained only for as long as necessary to support the Companion's function and your learning experience</li>
            </ul>
            <p className="text-gray-700">
              You may request deletion of your data at any time (see Section 9).
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Anonymised and aggregated insights</h2>
            <p className="text-gray-700 mb-4">
              To improve the Companion and the wider 30-Day Skills system, anonymised and aggregated data may be used to identify general leadership themes and learning needs.
            </p>
            <p className="text-gray-700 mb-4">This information:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Cannot be traced back to individuals</li>
              <li>Is never shared in identifiable form</li>
              <li>Is used only to improve learning content and system design</li>
            </ul>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. AI and automated processing</h2>
            <p className="text-gray-700 mb-4">The Companion uses automated processing to generate responses.</p>
            <p className="text-gray-700 mb-4">Important clarifications:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>No automated decisions are made that have legal or similarly significant effects</li>
              <li>The Companion does not replace human decision-making</li>
              <li>You remain fully responsible for any actions you choose to take</li>
            </ul>
            <p className="text-gray-700">
              The system is intentionally designed to <strong>support reflection, not instruction</strong>.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Confidentiality and access</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Access to stored data is strictly limited</li>
              <li>No data is shared with employers, colleagues, or third parties</li>
              <li>Data is never sold or used for advertising purposes</li>
            </ul>
            <p className="text-gray-700">
              The Companion exists solely to support your learning and development.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Lawful basis for processing (UK & EU GDPR)</h2>
            <p className="text-gray-700 mb-4">Data is processed under the following lawful bases:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Consent</strong> — you choose to use the Companion and share information</li>
              <li><strong>Legitimate interests</strong> — providing reflective learning support and improving the system</li>
            </ul>
            <p className="text-gray-700">
              Where consent is the lawful basis, it may be withdrawn at any time.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Your rights</h2>
            <p className="text-gray-700 mb-4">Under UK GDPR and EU GDPR, you have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Access your data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Restrict or object to certain processing</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="text-gray-700">
              Requests can be made via:<br />
              <strong>📧 privacy@30dayskills.co.uk</strong>
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Ethical boundaries</h2>
            <p className="text-gray-700 mb-4">The Companion is governed by explicit ethical constraints.</p>
            <p className="text-gray-700 mb-4">It will not:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Act as a surveillance tool</li>
              <li>Evaluate staff or colleagues</li>
              <li>Replace human judgment</li>
              <li>Be used to make employment decisions</li>
            </ul>
            <p className="text-gray-700">
              Its sole purpose is to support thoughtful, responsible leadership development.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to this notice</h2>
            <p className="text-gray-700 mb-4">
              This notice may be updated as the Companion evolves. Any material changes will be clearly communicated before taking effect.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <div className="bg-slate-900 text-white p-6 rounded-lg text-center">
            <p className="text-lg italic m-0">
              Trust is foundational to leadership — and to this system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionPrivacy;
